import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';

export default function Phase4ContractValue({ wizardData, setWizardData, onNext, onPrev }) {
  const value = wizardData.contractValue ? parseFloat(wizardData.contractValue.replace(/,/g, '')) : 0;
  const procType = wizardData.typeSelectionMethod === 'ai' 
    ? wizardData.aiRecommendedType 
    : wizardData.procurementType;
  
  // For mixed procurement, use the primary purpose to determine routing
  const effectiveProcType = procType === 'mixed' ? wizardData.primaryPurpose : procType;

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="contractValue" className="text-lg font-semibold mb-2 block">What is the Approved Budget for the Contract (ABC)?</Label>
        <p className="text-sm text-slate-500 mb-4">Enter the contract value in Philippine Pesos (₱)</p>
        <Input
          id="contractValue"
          type="text"
          placeholder="Enter amount (e.g., 5,000,000)"
          value={wizardData.contractValue}
          onChange={(e) => {
            const value = e.target.value.replace(/[^\d,]/g, '');
            setWizardData({ ...wizardData, contractValue: value });
          }}
          className="text-lg"
        />
        
        {/* ABC Threshold Reference Info */}
        {value > 0 && (
          <>
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <p className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-2">
                📋 ABC Threshold Reference:
              </p>
              <div className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                <p>• <strong>Direct Acquisition:</strong> Up to ₱200,000</p>
                <p>• <strong>Small Value Procurement (SVP):</strong> Up to ₱2,000,000</p>
                <p>• <strong>Community Participation (Goods):</strong> Up to ₱5,000,000</p>
                <p>• <strong>Community Participation (Infrastructure):</strong> Up to ₱10,000,000</p>
                <p>• <strong>Your ABC:</strong> ₱{value.toLocaleString()}</p>
              </div>
            </div>
            
            {/* LGU GPPB Review Thresholds */}
            {wizardData.entityType === 'lgu' && value <= 2000000 && (
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
                <p className="text-sm font-bold text-amber-900 dark:text-amber-300 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  LGU Small Value Procurement Thresholds
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mb-3 italic">
                  Maximum amounts subject to GPPB review for Small Value Procurement
                </p>
                <div className="space-y-3 text-xs text-amber-800 dark:text-amber-400">
                  <div className="border-b border-amber-200 dark:border-amber-700 pb-2">
                    <p className="font-semibold mb-1">1st & 2nd Class:</p>
                    <div className="ml-3 space-y-0.5">
                      <p>• Province: ₱2,000,000</p>
                      <p>• City: ₱2,000,000</p>
                      <p>• Municipality: ₱400,000</p>
                    </div>
                  </div>
                  <div className="border-b border-amber-200 dark:border-amber-700 pb-2">
                    <p className="font-semibold mb-1">3rd Class:</p>
                    <div className="ml-3 space-y-0.5">
                      <p>• Province: ₱2,000,000</p>
                      <p>• City: ₱1,600,000</p>
                      <p>• Municipality: ₱400,000</p>
                    </div>
                  </div>
                  <div className="border-b border-amber-200 dark:border-amber-700 pb-2">
                    <p className="font-semibold mb-1">4th Class:</p>
                    <div className="ml-3 space-y-0.5">
                      <p>• Province: ₱1,600,000</p>
                      <p>• City: ₱1,200,000</p>
                      <p>• Municipality: ₱200,000</p>
                    </div>
                  </div>
                  <div className="border-b border-amber-200 dark:border-amber-700 pb-2">
                    <p className="font-semibold mb-1">5th Class:</p>
                    <div className="ml-3 space-y-0.5">
                      <p>• Province: ₱1,200,000</p>
                      <p>• City: ₱800,000</p>
                      <p>• Municipality: ₱200,000</p>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold">Barangay: ₱100,000</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Special question for Goods: CSE or Non-CSE */}
        {effectiveProcType === 'goods' && value > 0 && (
          <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
            <Label className="font-semibold text-sm text-purple-900 dark:text-purple-300 mb-2 block">
              Is this for Common-use Supplies and Equipment (CSE)?
            </Label>
            <p className="text-xs text-purple-700 dark:text-purple-400 mb-3">
              CSE items available from PS-DBM must be procured through PS-DBM. Non-CSE items use standard procurement modes.
            </p>
            <RadioGroup 
              value={wizardData.isCSE === true ? 'cse' : wizardData.isCSE === false ? 'non-cse' : ''}
              onValueChange={(value) => setWizardData({ ...wizardData, isCSE: value === 'cse' })}
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cse" id="item-cse" />
                  <Label htmlFor="item-cse" className="cursor-pointer text-sm">
                    Yes, CSE items (available from PS-DBM)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non-cse" id="item-non-cse" />
                  <Label htmlFor="item-non-cse" className="cursor-pointer text-sm">
                    No, Non-CSE items (standard procurement)
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {wizardData.isCSE === true && (
              <div className="mt-3 space-y-3">
                <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded">
                  <p className="text-xs text-amber-800 dark:text-amber-300 font-medium mb-2">
                    ⚠️ CSE items: Procurement from PS-DBM is mandatory when available.
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    If you have justification for NOT using PS-DBM, check applicable reason(s) below:
                  </p>
                </div>
                
                <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded">
                  <p className="text-xs font-semibold mb-2">Justification for Non-PS-DBM Procurement:</p>
                  <div className="space-y-2">
                    {[
                      { key: 'unavailableInCatalog', label: 'Unavailability of required CSE in the PhilGEPS electronic catalog' },
                      { key: 'cannotMeetSpecs', label: 'Failure to meet required technical specifications or functional requirements' },
                      { key: 'insufficientQuantity', label: 'Insufficient available quantity relative to actual requirement' },
                      { key: 'inefficiency', label: 'Inefficiency of centralized procurement (slower process)' },
                      { key: 'impracticality', label: 'Impracticality due to significantly higher total cost (cheaper price elsewhere)' },
                      { key: 'economicNonViability', label: 'Economic non-viability or sustainability concerns (wasteful, not worth the money)' }
                    ].map((option) => (
                      <div key={option.key} className="flex items-start space-x-2">
                        <Checkbox
                          id={`cse-${option.key}`}
                          checked={wizardData.cseJustification[option.key]}
                          onCheckedChange={() => {
                            setWizardData(prev => ({
                              ...prev,
                              cseJustification: {
                                ...prev.cseJustification,
                                [option.key]: !prev.cseJustification[option.key]
                              }
                            }));
                          }}
                        />
                        <Label htmlFor={`cse-${option.key}`} className="cursor-pointer text-xs leading-relaxed">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  
                  {Object.values(wizardData.cseJustification).some(v => v) && (
                    <div className="mt-3 p-2 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded">
                      <p className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                        ✓ Item is exempt from centralized procurement; proceed to standard procurement modes.
                      </p>
                    </div>
                  )}
                  
                  {!Object.values(wizardData.cseJustification).some(v => v) && (
                    <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded">
                      <p className="text-xs text-red-800 dark:text-red-300 font-semibold">
                        🛑 No justification selected - Procurement from PS-DBM is mandatory.
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                        This CSE item must be procured through PS-DBM. The wizard ends here for this procurement.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {wizardData.isCSE === false && (
              <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded">
                <p className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                  ✓ Non-CSE items: Standard procurement modes apply based on your ABC and conditions.
                </p>
              </div>
            )}
            {(wizardData.isCSE === undefined || wizardData.isCSE === null) && (
              <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded">
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                  ⚠️ Please select an option above to proceed.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Special question for Infrastructure: Community-based project */}
        {effectiveProcType === 'infrastructure' && value > 0 && value <= 10000000 && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <Label className="font-semibold text-sm text-blue-900 dark:text-blue-300 mb-2 block">
              Community-Based Infrastructure Project?
            </Label>
            <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
              For simple infrastructure projects up to ₱10,000,000 undertaken with community participation
            </p>
            <RadioGroup 
              value={wizardData.isCommunityBased === true ? 'yes' : wizardData.isCommunityBased === false ? 'no' : ''}
              onValueChange={(value) => setWizardData({ ...wizardData, isCommunityBased: value === 'yes' })}
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="community-yes" />
                  <Label htmlFor="community-yes" className="cursor-pointer text-sm">
                    Yes, this is a community-based simple infrastructure project
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="community-no" />
                  <Label htmlFor="community-no" className="cursor-pointer text-sm">
                    No, this is a standard infrastructure project
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {wizardData.isCommunityBased === true && (
              <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded">
                <p className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                  ✓ Negotiated Procurement - Community Participation is available for this project (up to ₱10M)
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} size="lg">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {/* 
          Next button logic for Goods procurement:
          - If CSE question is shown but not answered (isCSE is undefined/null): hide Next
          - If CSE is true but no justification: hide Next (must use PS-DBM)
          - If CSE is false (Non-CSE): show Next
          - If CSE is true with justification: show Next
          For non-Goods: show Next as usual
        */}
        {(() => {
          const showCSEQuestion = effectiveProcType === 'goods' && value > 0;
          
          if (showCSEQuestion) {
            // CSE question is shown - must be answered
            if (wizardData.isCSE === undefined || wizardData.isCSE === null) {
              // Not answered yet - hide Next
              return null;
            }
            if (wizardData.isCSE === true && !Object.values(wizardData.cseJustification).some(v => v)) {
              // CSE selected but no justification - hide Next (must use PS-DBM)
              return null;
            }
          }
          
          // Show Next button
          return (
            <Button onClick={onNext} className="flex-1 bg-emerald-600 hover:bg-emerald-700" size="lg">
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          );
        })()}
      </div>
    </div>
  );
}
